import defaults from "lodash/defaults";
import moment from "moment";
import { DATE_FORMATS } from "../../../constants";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import CreatePlanModal from "../../../parts/modals/create-nutrition-plan";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import AddGoal from "../../../parts/modals/profile/AddGoal";
import { resolveExerciseSetVideoModal } from "../../../utils/video-modal-url";
import CopyMealPlanFromTemplateModal from "../../coaching-portal/all-templates/modals/CopyMealPlanFromTemplateModal";
import HandleMealPlanOptionNameModal from "../../coaching-portal/all-templates/modals/HandleMealPlanOptionNameModal";
import HandleMealPlanTemplateModal from "../../coaching-portal/all-templates/modals/HandleMealPlanTemplateModal";
import ModifyWithAIPlanModal from "../../coaching-portal/all-templates/modals/ModifyWithAIPlanModal";
import RegenerateMealPlanModal from "../../coaching-portal/all-templates/modals/RegenerateMealPlanModal";
import PreviewPhotoModal from "../calendar/progress-photos/components/PreviewPhotoModal";
import { getPhotoThreadMapForDay } from "../calendar/utils/meal-photo-helpers";
import { MealPlannerContainer } from "./meal-planner/MealPlannerContainer";
import { AddFoodModal } from "./meal-planner/modals/AddFoodModal";
import { BuildMealModal } from "./meal-planner/modals/BuildMealModal";
import { CreateMealPlanModal } from "./meal-planner/modals/CreateMealPlanModal";
import { CreateNewFoodModal } from "./meal-planner/modals/CreateNewFoodModal";
import { CustomMealModal } from "./meal-planner/modals/CustomMealModal";
import DataSourceModal from "./meal-planner/modals/DataSourceModal";
import { DuplicateMealPlanModal } from "./meal-planner/modals/DuplicateMealPlanModal";
import { DuplicateMealPlanWeekModal } from "./meal-planner/modals/DuplicateMealPlanWeekModal";
import { HandleFoodModal } from "./meal-planner/modals/HandleFoodModal";
import { MoveMealPlanModal } from "./meal-planner/modals/MoveMealPlanModal";
import { RecipeDetailModal } from "./meal-planner/modals/RecipeDetailModal";
import ReplaceDayFromTemplateModal from "./meal-planner/modals/ReplaceDayFromTemplateModal";
import ReplaceOptionFromTemplateModal from "./meal-planner/modals/ReplaceOptionFromTemplateModal";
import { SaveMealPlanAsTemplateModal } from "./meal-planner/modals/SaveMealPlanAsTemplateModal";
import Nutrition from "./Nutrition";

const NutritionController = (app) => {
  app.get("/", async ({ req, res, load, locals, api }) => {
    const { query } = req;
    const { section, plan, mealPlan } = query;
    const modal = query.modal || {};
    const { organizationRoleName } = locals;
    const planMode = query.planMode || "detailed"; // Default to "detailed" if not specified

    defaults(query, {
      date: moment().format("YYYY-MM-DD"),
      utcDate: Number(moment().format("YYYYMMDD")),
    });

    const { athlete, showBack, dataView } = query;

    let comesFromBodyComp = false;

    // Required conditional because this could be undefined when refreshing
    if (req?.original?.headers?.referer) {
      comesFromBodyComp =
        req.original.headers.referer.includes("body-composition");
    }
    if (showBack) comesFromBodyComp = true;
    locals.comesFromBodyComp = comesFromBodyComp;

    const isAllowToLoadAllAthletes =
      organizationRoleName &&
      (organizationRoleName === "Nutritionist" ||
        organizationRoleName === "Admin" ||
        organizationRoleName === "Owner");

    const loadPromises = [
      load("dailyStats", {
        proxyAthlete: athlete,
      }),
      load("nutritionPlans", {
        proxyAthlete: athlete,
      }),
      load("clientGoals", {
        proxyAthlete: athlete,
      }),
      load("tdeeOverride", {
        proxyAthlete: athlete,
      }),
      // Nutrition Progress is a reporting surface (Table/Calendar/Log), never
      // shows dates past today — end only needs a small forward buffer to
      // cover ISO-week widening (Calendar/Log pad the visible week's tail
      // past today when "today" falls mid-week), not a full extra year.
      load("activeExercises", {
        start: moment().subtract(1, "year").startOf("day"),
        end: moment().add(7, "days").endOf("day"),
        proxyAthlete: athlete,
      }),
      // Powers the Log view's PR badge on LogTrainingCard.
      // Unlike activeExercises, this loader bails with {} if `end` is in the
      // future (see load/workout-stats.js), so it's capped at today instead
      // of +1 year.
      load("dayPersonalRecords", {
        start: moment()
          .subtract(1, "year")
          .startOf("day")
          .format(DATE_FORMATS.UTC),
        end: moment().endOf("day").format(DATE_FORMATS.UTC),
        proxyAthlete: athlete,
        isCheckIn: true,
      }),
      // Powers the Log view's Habits card — resolved habit checklist
      // per day (coach/org template + DailyStat.accountabilities merge),
      // via a narrow endpoint mirroring calendar-log.js's own computation
      // for the athlete's calendar Log (see msb-api/lib/api/habit-logs.js).
      load("habitLogs", {
        start: moment().subtract(1, "year").startOf("day"),
        end: moment().endOf("day"),
        proxyAthlete: athlete,
      }),
      load("femaleCycleLogs", {
        proxyAthlete: athlete,
      }),
      load("femaleCycleSettings", {
        proxyAthlete: athlete,
      }),
      load("nutritionTableTemplates"),
      ...(locals.activeUser.organizationId &&
      isAllowToLoadAllAthletes &&
      !locals.allAthletesFromOrganization
        ? [load("allAthletesFromOrganization")]
        : []),
    ];

    // Load meal photos when mealPhotos view is active
    if (dataView === "mealPhotos" && query.utcDate) {
      const dateStr = query.utcDate.toString();

      loadPromises.push(
        load("mealPhotos", {
          startDate: dateStr,
          endDate: dateStr,
        })
      );
    }

    await Promise.all(loadPromises);

    // "View Video" (exercise-set-video), reused from Calendar's
    // LogTrainingCard - see resolveExerciseSetVideoModal's own doc comment
    // for why this needs more than a plain prop pass-through.
    const { redirectParams, resolvedThreadId, prevHref, nextHref } =
      await resolveExerciseSetVideoModal({
        query,
        modal,
        locals,
        load,
        athlete,
      });
    if (redirectParams) return res.refreshWith(redirectParams);

    // Progress-photo comment thread for the "preview-photo" modal (Log's
    // LogProgressPhotosCard) — CommentsSection reads locals.comments, it
    // doesn't fetch anything itself. Mirrors progress-photos-controller.js's
    // own handling verbatim.
    if (modal.type === "preview-photo" && query.threadId) {
      await load("comments", { threadId: query.threadId });
    }

    // Meal-photo comment threads for the currently open PhotosCarousel modal.
    // The Table/Log views key off dailyStats — a flat array here, unlike
    // Calendar's date-grouped logMealPhotos — while the classic summary's
    // MealPhotosDisplay renders straight from the mealPhotos loader.
    if (query.dailyStatId) {
      const matchesDailyStatId = (day) =>
        String(day._id) === String(query.dailyStatId);
      const mealPhotosDay =
        locals.mealPhotos?.days?.find(matchesDailyStatId) ??
        locals.dailyStats?.find(matchesDailyStatId);
      const photoThreadMap = mealPhotosDay
        ? getPhotoThreadMapForDay(mealPhotosDay)
        : [];
      if (photoThreadMap.length > 0) {
        await load("mealComments", { photoThreadMap });
      } else {
        delete locals.mealComments;
      }
    } else {
      delete locals.mealComments;
    }

    //init of meal plan
    if (section && plan && mealPlan) {
      const mealPlanPromises = [
        load("mealPlan", {
          mealPlanId: mealPlan,
          proxyAthlete: athlete,
        }),
        load("basicMealsInformation"),
      ];

      if (locals.activeUser?.organizationId) {
        mealPlanPromises.push(
          load("usersOrganization", {
            organizationId: locals.activeUser.organizationId,
          })
        );
      }

      await Promise.all(mealPlanPromises);
      //we are request it after the promise all to have the organization information already loaded

      await Promise.all([
        load("mostUsedMealPlans"),
        load("mealPlanIsTemplate", { mealPlanId: mealPlan }),
      ]);
    }
    //end of meal plan

    if (locals.nutritionPlans) {
      locals.currentNutritionPlan = locals.nutritionPlans.find(
        (plan) =>
          plan.utcStartDate <= query.utcDate && plan.utcEndDate >= query.utcDate
      );
    }

    const editPlan = query.plan
      ? locals.nutritionPlans.find((plan) => plan._id === query.plan)
      : null;

    const copy = Boolean(query.copy);

    let copyPlanDates = {};

    if (editPlan && copy) {
      const lastPlan = locals.nutritionPlans.sort(
        (a, b) => b.utcEndDate - a.utcEndDate
      );

      const todayUtc = moment().format("YYYYMMDD");

      const lastPlanEndDate = moment(String(lastPlan[0].utcEndDate));

      const isBefore = lastPlanEndDate.isBefore(todayUtc);

      const startDay = moment(String(editPlan.utcStartDate)).toDate();
      const endDay = moment(String(editPlan.utcEndDate)).toDate();

      const numberOfDays = moment(endDay).diff(startDay, "days");

      if (isBefore) {
        copyPlanDates = {
          startDate: Number(todayUtc),
          endDate: Number(
            moment(todayUtc).add(numberOfDays, "days").format("YYYYMMDD")
          ),
        };
      } else {
        copyPlanDates = {
          startDate: Number(
            moment(String(lastPlan[0].utcEndDate))
              .add(1, "days")
              .format("YYYYMMDD")
          ),
          endDate: Number(
            moment(String(lastPlan[0].utcEndDate))
              .add(numberOfDays + 1, "days")
              .format("YYYYMMDD")
          ),
        };
      }
    }

    const nextMealPlanPromises = [];
    switch (modal.type) {
      case "copy-meal-plan-from-template":
      case "modify-with-ai":
      case "regenerate-meal-plan":
        nextMealPlanPromises.push(
          load("mealPlanTemplates", {
            customLimit: 1,
            customSkip: 1,
            noPagination: true,
            planMode,
          }),
          load("assignedMealPlans")
        );
        break;
      case "handle-meal-plan":
        nextMealPlanPromises.push(
          load("mealPlanTemplates", {
            customLimit: 1,
            customSkip: 1,
            noPagination: true,
            planMode: query.planMode || "detailed",
          })
        );
        break;
      case "replace-option-from-template":
      case "replace-day-from-template":
        nextMealPlanPromises.push(
          load("mealPlanTemplates", {
            customLimit: 1,
            customSkip: 1,
            noPagination: true,
            planMode: "simplified",
          })
        );
        break;
      case "create-meal-planner-food":
      case "build-meal":
        nextMealPlanPromises.push(load("customFoods"));
        break;
      case "recipe-detail":
        nextMealPlanPromises.push(
          load("recipeDetails", {
            type: query.recipeType,
            recipeId: query.recipeId,
          })
        );
        break;
      default:
        break;
    }
    await Promise.all(nextMealPlanPromises);

    locals.modal = {
      "add-client-goal": <AddGoal locals={locals} />, // "+ Set bodyweight goal" on the Bodyweight trend card
      "create-nutrition-plan": (
        <CreatePlanModal
          editPlan={editPlan}
          copy={copy}
          copyPlanDates={copyPlanDates}
        />
      ),
      "create-meal-planner-food": <AddFoodModal locals={locals} api={api} />, //main modal
      "handle-meal-planner-food": <HandleFoodModal locals={locals} />, //create-edit a food
      "create-new-food": <CreateNewFoodModal />, //modal to show 2 options (create a food or meal)
      "duplicate-meal-plan": <DuplicateMealPlanModal locals={locals} />, //duplicate a meal plan
      "duplicate-meal-plan-week": (
        <DuplicateMealPlanWeekModal locals={locals} />
      ), //duplicate a meal plan week
      "move-meal-plan": <MoveMealPlanModal locals={locals} />, //move a meal plan
      "custom-meal": <CustomMealModal locals={locals} />, //modal to create cutom meals (something different as "snacks", "lunch", "breakfast")
      "build-meal": <BuildMealModal locals={locals} api={api} />, //create and edit a meal
      "recipe-detail": <RecipeDetailModal locals={locals} />, //read-only recipe details
      "save-as-template": <SaveMealPlanAsTemplateModal locals={locals} />,
      "data-source": <DataSourceModal locals={locals} />,
      "activity-feed": <ActivityDrawer />,
      "copy-meal-plan-from-template": (
        <CopyMealPlanFromTemplateModal locals={locals} />
      ),
      "modify-with-ai": <ModifyWithAIPlanModal locals={locals} />,
      "regenerate-meal-plan": <RegenerateMealPlanModal locals={locals} />,
      //simplified meal plans
      "create-meal-plan": <CreateMealPlanModal locals={locals} />,
      "handle-meal-plan-option-name": (
        <HandleMealPlanOptionNameModal locals={locals} />
      ),
      "handle-meal-plan": <HandleMealPlanTemplateModal locals={locals} />,
      "replace-option-from-template": (
        <ReplaceOptionFromTemplateModal locals={locals} />
      ),
      "replace-day-from-template": (
        <ReplaceDayFromTemplateModal locals={locals} />
      ),
      "preview-photo": (
        <PreviewPhotoModal
          dailyStatId={query.dailyStatId}
          photo={{ type: query.photoType, pictureImg: query.pictureImg }}
          threadId={query.threadId}
          locals={locals}
          utcDate={query.utcDate}
        />
      ),
      "exercise-set-video": (
        <JWPlayerVideoModal
          athleteId={query.uploaderId || athlete}
          exerciseToEdit={query.exerciseToEdit}
          locals={locals}
          mediaId={query.mediaId}
          movement={query.movement}
          originalId={query.originalId}
          primary={query.primary}
          reviewedByTheCoach={query.reviewedByTheCoach}
          secondary={query.secondary}
          setIndex={query.setIndex}
          threadId={resolvedThreadId}
          type={query.type}
          utcDate={query.utcDate}
          videoIndex={query.videoIndex}
          prevHref={prevHref}
          nextHref={nextHref}
          setNumber={query.setNumber}
          setTarget={query.setTarget}
          setComment={query.setComment}
        />
      ),
    }[modal.type];

    const sections = {
      main: <Nutrition locals={locals} />,
      "meal-planner": <MealPlannerContainer locals={locals} api={api} />,
    };

    res.body = sections[query.section || "main"];
  });
};

export default NutritionController;
